import React from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useExtratoFinanceiro } from '../../../hooks/useExtratoFinanceiro';
import { TabelaExtrato } from '../../../components/financeiro';
import type { ExtratoLinha, PeriodoFinanceiro, TipoExtratoLinha } from '../../../types/financeiro';

// Orquestra o hook (useExtratoFinanceiro) + os controles de filtro + <TabelaExtrato>, e converte
// as ações da tabela em navegação (a page navega, não o componente — ai/architecture.core.md
// §3.3/§5). Recebe `periodo` do hub (FinanceiroHub), como ResumoTab.

export interface ExtratoTabProps {
  periodo: PeriodoFinanceiro;
}

const TIPO_OPCOES: { value: TipoExtratoLinha; label: string }[] = [
  { value: 'INSCRICAO', label: 'Inscrição' },
  { value: 'AGENDAMENTO', label: 'Agendamento' },
  { value: 'REEMBOLSO', label: 'Reembolso' },
];

// União dos 3 vocabulários de status distintos das 3 fontes (StatusPagamento,
// StatusPedidoAgendamento, StatusReembolso — ver `## Notes` do plano da Phase 4). O valor de
// cada opção é o nome cru do enum, o único valor não-ambíguo que a AC-8 fixa.
//
// Deliberadamente NÃO oferecidos: EM_PROCESSAMENTO, PARCIAL, RECUSADO e EXPIRADO — são exatamente
// os 4 valores que a decisão de produto de 2026-07-31 marcou para sair do StatusPagamento (o
// enum canônico tem 6: PENDENTE, PAGO, GRATUITO, CANCELADO, ESTORNADO, CHARGEBACK), então
// filtrar por eles só alcançaria linhas legadas. GRATUITO sai junto por decisão de produto:
// inscrição sem cobrança não é lançamento que o atleta procure no extrato.
//
// Efeito colateral consciente de tirar RECUSADO: ele é compartilhado com StatusReembolso, onde
// NÃO é legado — reembolso recusado deixa de ser filtrável (segue aparecendo na listagem sem
// filtro e na aba Reembolsos).
const STATUS_OPCOES = [
  'PENDENTE',
  'PAGO',
  'CANCELADO',
  'ESTORNADO',
  'CHARGEBACK',
  'ABERTO',
  'AGUARDANDO_PAGAMENTO',
  'APROVADO',
  'FINALIZADO',
];

function formatDataBr(iso: string): string {
  return dayjs(iso).format('DD/MM/YYYY');
}

function rotaDetalhe(linha: ExtratoLinha): string {
  switch (linha.tipo) {
    case 'INSCRICAO':
      return `/athlete/payment/${linha.referenciaId}`;
    case 'AGENDAMENTO':
      return `/quadras/pedido/${linha.referenciaId}`;
    case 'REEMBOLSO':
    default:
      return `/athlete/payments/${linha.referenciaId}/refunds`;
  }
}

const ExtratoTab: React.FC<ExtratoTabProps> = ({ periodo }) => {
  const navigate = useNavigate();
  const {
    linhas,
    page,
    size,
    totalItems,
    filtros,
    setFiltros,
    setPage,
    setSize,
    loading,
    error,
    refetch,
  } = useExtratoFinanceiro(periodo);

  // Select simples (uma opção por vez), com "" = Todos. O contrato do hook e da query segue sendo
  // lista (`tipos`/`status`), que o useExtratoFinanceiro converte em `null` quando vazia — então
  // "Todos" é lista vazia, e uma escolha é lista de um item. Nada muda no backend.
  const handleTipoChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const valor = event.target.value as TipoExtratoLinha | '';
    setFiltros({ ...filtros, tipos: valor ? [valor] : [] });
  };

  const handleStatusChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const valor = event.target.value;
    setFiltros({ ...filtros, status: valor ? [valor] : [] });
  };

  const handleVerDetalhe = (linha: ExtratoLinha) => {
    navigate(rotaDetalhe(linha));
  };

  const handleVerRecibo = (linha: ExtratoLinha) => {
    if (linha.reciboUrl) {
      window.open(linha.reciboUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // AC-12: falha de rede — mensagem amigável + ação de repetir, sem crash e sem catch silencioso.
  if (error) {
    return (
      <div className="text-center py-5" data-testid="extrato-erro">
        <p className="text-danger mb-3">Não foi possível carregar o extrato. Tente novamente.</p>
        <button type="button" className="btn btn-primary" onClick={() => refetch()}>
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="row g-3 mb-3">
        <div className="col-auto">
          <label htmlFor="extrato-filtro-tipo" className="form-label d-block">
            Tipo
          </label>
          <select
            id="extrato-filtro-tipo"
            className="form-select"
            value={filtros.tipos[0] ?? ''}
            onChange={handleTipoChange}
          >
            <option value="">Todos</option>
            {TIPO_OPCOES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-auto">
          <label htmlFor="extrato-filtro-status" className="form-label d-block">
            Status
          </label>
          <select
            id="extrato-filtro-status"
            className="form-select"
            value={filtros.status[0] ?? ''}
            onChange={handleStatusChange}
          >
            <option value="">Todos</option>
            {STATUS_OPCOES.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      </div>

      {!loading && linhas.length === 0 ? (
        <div className="text-center py-5" data-testid="extrato-vazio">
          <p className="mb-0">
            Nenhum lançamento entre {formatDataBr(periodo.inicio)} e {formatDataBr(periodo.fim)} com os
            filtros selecionados.
          </p>
        </div>
      ) : (
        <TabelaExtrato
          linhas={linhas}
          page={page}
          size={size}
          totalItems={totalItems}
          loading={loading}
          error={error}
          onPageChange={setPage}
          onSizeChange={setSize}
          onVerRecibo={handleVerRecibo}
          onVerDetalhe={handleVerDetalhe}
        />
      )}
    </div>
  );
};

export default ExtratoTab;
